import React, { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon, Check as CheckIcon, Close as CloseIcon } from "@mui/icons-material";

const Cells = ({ component, onUpdate, isSelected, mode, value, onChange, readOnly }) => {
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");

  const rows = component.rows || 3;
  const columns = component.columns || 3;
  // En modo respuesta, usar value (answers) como datos de celdas, si no usar cellData
  const cellData = mode === "response" ? value || {} : component.cellData || {};
  const showHeaders = component.showHeaders !== false;
  const headerRow =
    component.headerRow ||
    Array(columns)
      .fill("")
      .map((_, i) => `Columna ${i + 1}`);

  const handleCellDoubleClick = (rowIndex, colIndex) => {
    if (mode === "response" && readOnly) return;
    const cellKey = `${rowIndex}-${colIndex}`;
    const currentValue = cellData[cellKey] || "";
    setEditingCell({ rowIndex, colIndex });
    setEditValue(currentValue);
  };

  const handleCellSave = () => {
    if (editingCell) {
      const cellKey = `${editingCell.rowIndex}-${editingCell.colIndex}`;
      const newCellData = { ...cellData, [cellKey]: editValue };
      if (mode === "response") {
        // Guardar en answers
        onChange && onChange(newCellData);
      } else {
        // Guardar en plantilla
        onUpdate?.({ cellData: newCellData });
      }
      setEditingCell(null);
      setEditValue("");
    }
  };

  const handleCellKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleCellSave();
    } else if (e.key === "Escape") {
      setEditingCell(null);
      setEditValue("");
    }
  };

  const handleCellBlur = () => {
    handleCellSave();
  };

  const handleHeaderEdit = (colIndex) => {
    const newHeaderRow = [...headerRow];
    newHeaderRow[colIndex] = editValue;
    onUpdate?.({ headerRow: newHeaderRow });
    setEditingCell(null);
    setEditValue("");
  };

  const handleHeaderKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (editingCell && editingCell.rowIndex === "header") {
        handleHeaderEdit(editingCell.colIndex);
      }
    } else if (e.key === "Escape") {
      setEditingCell(null);
      setEditValue("");
    }
  };

  const handleHeaderBlur = () => {
    if (editingCell && editingCell.rowIndex === "header") {
      handleHeaderEdit(editingCell.colIndex);
    }
  };

  const handleAddRow = () => {
    const newRows = rows + 1;
    onUpdate?.({ rows: newRows });
  };

  const handleAddColumn = () => {
    const newColumns = columns + 1;
    const newHeaderRow = [...headerRow, `Columna ${newColumns}`];
    onUpdate?.({
      columns: newColumns,
      headerRow: newHeaderRow,
    });
  };

  const handleDeleteRow = (rowIndex) => {
    if (rows > 1) {
      const newRows = rows - 1;
      const newCellData = { ...cellData };

      // Eliminar datos de la fila eliminada
      for (let col = 0; col < columns; col++) {
        delete newCellData[`${rowIndex}-${col}`];
      }

      // Reindexar las filas posteriores
      for (let row = rowIndex + 1; row < rows; row++) {
        for (let col = 0; col < columns; col++) {
          const oldKey = `${row}-${col}`;
          const newKey = `${row - 1}-${col}`;
          if (newCellData[oldKey] !== undefined) {
            newCellData[newKey] = newCellData[oldKey];
            delete newCellData[oldKey];
          }
        }
      }

      onUpdate?.({
        rows: newRows,
        cellData: newCellData,
      });
    }
  };

  const handleDeleteColumn = (colIndex) => {
    if (columns > 1) {
      const newColumns = columns - 1;
      const newHeaderRow = headerRow.filter((_, i) => i !== colIndex);
      const newCellData = { ...cellData };

      // Eliminar datos de la columna eliminada
      for (let row = 0; row < rows; row++) {
        delete newCellData[`${row}-${colIndex}`];
      }

      // Reindexar las columnas posteriores
      for (let row = 0; row < rows; row++) {
        for (let col = colIndex + 1; col < columns; col++) {
          const oldKey = `${row}-${col}`;
          const newKey = `${row}-${col - 1}`;
          if (newCellData[oldKey] !== undefined) {
            newCellData[newKey] = newCellData[oldKey];
            delete newCellData[oldKey];
          }
        }
      }

      onUpdate?.({
        columns: newColumns,
        headerRow: newHeaderRow,
        cellData: newCellData,
      });
    }
  };

  const getCellValue = (rowIndex, colIndex) => {
    const cellKey = `${rowIndex}-${colIndex}`;
    return cellData[cellKey] || "";
  };

  const isEditing = (rowIndex, colIndex) => {
    return editingCell && editingCell.rowIndex === rowIndex && editingCell.colIndex === colIndex;
  };

  return (
    <Box sx={{ width: "100%", height: "100%", p: 0 }}>
      <TableContainer
        component={Paper}
        sx={{
          width: "100%",
          maxHeight: "100%",
          border: "1px solid #e0e0e0",
          borderRadius: 1,
        }}
      >
        <Table size="small" sx={{ minWidth: 200, width: "100%", height: "100%" }}>
          {showHeaders && (
            <TableHead>
              <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                {headerRow.map((header, colIndex) => (
                  <TableCell
                    key={colIndex}
                    sx={{
                      fontWeight: "bold",
                      cursor: mode === "response" ? "default" : "pointer",
                      "&:hover": { bgcolor: mode === "response" ? undefined : "#e3f2fd" },
                      position: "relative",
                    }}
                    onClick={() => {
                      if (mode !== "response") {
                        setEditingCell({ rowIndex: "header", colIndex });
                        setEditValue(header);
                      }
                    }}
                  >
                    {isEditing("header", colIndex) ? (
                      <TextField
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        size="small"
                        autoFocus
                        onKeyDown={handleHeaderKeyDown}
                        onBlur={handleHeaderBlur}
                        sx={{ width: "100%" }}
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                      />
                    ) : (
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography variant="body2">{header}</Typography>
                        {mode !== "response" && isSelected && (
                          <Tooltip title="Eliminar columna">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteColumn(colIndex);
                              }}
                              sx={{ opacity: 0.6, "&:hover": { opacity: 1 } }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    )}
                  </TableCell>
                ))}
                {mode !== "response" && isSelected && (
                  <TableCell sx={{ width: 50, p: 0 }}>
                    <Tooltip title="Agregar columna">
                      <IconButton size="small" onClick={handleAddColumn}>
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {Array.from({ length: rows }).map((_, rowIndex) => (
              <TableRow key={rowIndex} sx={{ "&:hover": { bgcolor: "#fafafa" } }}>
                {Array.from({ length: columns }).map((_, colIndex) => (
                  <TableCell
                    key={colIndex}
                    sx={{
                      cursor: mode === "response" && !readOnly ? "pointer" : "pointer",
                      "&:hover": { bgcolor: mode === "response" && !readOnly ? "#e3f2fd" : "#e3f2fd" },
                      border: "1px solid #e0e0e0",
                      minWidth: 100,
                      height: 40,
                      textAlign: "center",
                    }}
                    onDoubleClick={() => {
                      if ((mode === "response" && !readOnly) || (mode !== "response" && isSelected)) {
                        handleCellDoubleClick(rowIndex, colIndex);
                      }
                    }}
                  >
                    {isEditing(rowIndex, colIndex) ? (
                      <TextField
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        size="small"
                        autoFocus
                        multiline
                        maxRows={3}
                        onKeyDown={handleCellKeyDown}
                        onBlur={handleCellBlur}
                        sx={{ width: "100%" }}
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          minHeight: 20,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          textAlign: "center",
                        }}
                      >
                        {getCellValue(rowIndex, colIndex)}
                      </Typography>
                    )}
                  </TableCell>
                ))}
                {mode !== "response" && isSelected && (
                  <TableCell sx={{ width: 50, p: 0 }}>
                    <Tooltip title="Eliminar fila">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteRow(rowIndex)}
                        sx={{ opacity: 0.6, "&:hover": { opacity: 1 } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {mode !== "response" && isSelected && (
              <TableRow>
                <TableCell
                  colSpan={columns + (isSelected ? 1 : 0)}
                  sx={{
                    textAlign: "center",
                    p: 1,
                    border: "none",
                  }}
                >
                  <Tooltip title="Agregar fila">
                    <IconButton size="small" onClick={handleAddRow}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Cells;
